import { GET_INDEXING_JOB_DETAILS_NAME } from "@/lib/ai/tools/get-indexing-job-details/constants";
import { GET_SEARCH_ANALYTICS_AGGREGATED_DATA_NAME } from "@/lib/ai/tools/get-search-analytics-aggregated-data/constants";
import { GET_SEARCH_ANALYTICS_RAW_DATA_NAME } from "@/lib/ai/tools/get-search-analytics-raw-data/constants";
import { GET_SESSION_DETAIL_NAME } from "@/lib/ai/tools/get-session-detail/constants";
import { GET_USER_ANALYTICS_AGGREGATED_DATA_NAME } from "@/lib/ai/tools/get-user-analytics-aggregated-data/constants";
import { GET_USER_ANALYTICS_KEY_EVENTS_NAME } from "@/lib/ai/tools/get-user-analytics-key-events/constants";
import { GET_USER_ANALYTICS_RAW_DATA_NAME } from "@/lib/ai/tools/get-user-analytics-raw-data/constants";
import { LIST_BIGQUERY_NAME } from "@/lib/ai/tools/list-bigquery/constants";
import { LIST_DASHBOARDS_NAME } from "@/lib/ai/tools/list-dashboards/constants";
import { LIST_INDEX_WORKERS_NAME } from "@/lib/ai/tools/list-index-workers/constants";
import { LIST_QUERY_GROUPS_NAME } from "@/lib/ai/tools/list-query-groups/constants";
import { LIST_SEARCH_ANALYTICS_VIEWS_NAME } from "@/lib/ai/tools/list-search-analytics-views/constants";
import { LIST_SESSIONS_NAME } from "@/lib/ai/tools/list-sessions/constants";
import { LIST_URL_GROUPS_NAME } from "@/lib/ai/tools/list-url-groups/constants";
import { LIST_USER_ANALYTICS_VIEWS_NAME } from "@/lib/ai/tools/list-user-analytics-views/constants";
import { VIZ_TOOL_NAME } from "@/lib/ai/visualization/config";
import {
  createVisualizationAdapterRegistry,
  defineVisualizationAdapter,
  type VisualizationAdapter,
  type VizSourceSnapshot,
  type VizSourceToolName,
} from "@/lib/ai/visualization/data/adapters/adapter-contract";
import { buildIndexingJobDetailsDatasets } from "@/lib/ai/visualization/data/adapters/get-indexing-job-details";
import { buildSaAggregatedDatasets } from "@/lib/ai/visualization/data/adapters/get-search-analytics-aggregated-data";
import { buildSaRawDatasets } from "@/lib/ai/visualization/data/adapters/get-search-analytics-raw-data";
import { buildUaSessionDetailDatasets } from "@/lib/ai/visualization/data/adapters/get-session-detail";
import { buildUaAggregatedDatasets } from "@/lib/ai/visualization/data/adapters/get-user-analytics-aggregated-data";
import { buildUaKeyEventsDatasets } from "@/lib/ai/visualization/data/adapters/get-user-analytics-key-events";
import { buildUaRawDatasets } from "@/lib/ai/visualization/data/adapters/get-user-analytics-raw-data";
import { buildListBigqueryDatasets } from "@/lib/ai/visualization/data/adapters/list-bigquery";
import { buildListDashboardsDatasets } from "@/lib/ai/visualization/data/adapters/list-dashboards";
import { buildListIndexWorkersDatasets } from "@/lib/ai/visualization/data/adapters/list-index-workers";
import { buildListQueryGroupsDatasets } from "@/lib/ai/visualization/data/adapters/list-query-groups";
import { buildListSearchAnalyticsViewsDatasets } from "@/lib/ai/visualization/data/adapters/list-search-analytics-views";
import { buildListSessionsDatasets } from "@/lib/ai/visualization/data/adapters/list-sessions";
import { buildListUrlGroupsDatasets } from "@/lib/ai/visualization/data/adapters/list-url-groups";
import { buildListUserAnalyticsViewsDatasets } from "@/lib/ai/visualization/data/adapters/list-user-analytics-views";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";

const visualizationAdapters = [
  defineVisualizationAdapter({
    toolName: GET_SEARCH_ANALYTICS_AGGREGATED_DATA_NAME,
    adapt: ({ output, source }) => buildSaAggregatedDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: GET_SEARCH_ANALYTICS_RAW_DATA_NAME,
    adapt: ({ output, source }) => buildSaRawDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: GET_SESSION_DETAIL_NAME,
    adapt: ({ output, source }) => buildUaSessionDetailDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: GET_USER_ANALYTICS_AGGREGATED_DATA_NAME,
    adapt: ({ output, source }) => buildUaAggregatedDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: GET_USER_ANALYTICS_KEY_EVENTS_NAME,
    adapt: ({ output, source }) => buildUaKeyEventsDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: GET_USER_ANALYTICS_RAW_DATA_NAME,
    adapt: ({ output, source }) => buildUaRawDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: GET_INDEXING_JOB_DETAILS_NAME,
    adapt: ({ output, source }) =>
      buildIndexingJobDetailsDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: LIST_BIGQUERY_NAME,
    adapt: ({ output, source }) => buildListBigqueryDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: LIST_DASHBOARDS_NAME,
    adapt: ({ output, source }) => buildListDashboardsDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: LIST_INDEX_WORKERS_NAME,
    adapt: ({ output, source }) =>
      buildListIndexWorkersDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: LIST_QUERY_GROUPS_NAME,
    adapt: ({ output, source }) => buildListQueryGroupsDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: LIST_SEARCH_ANALYTICS_VIEWS_NAME,
    adapt: ({ output, source }) =>
      buildListSearchAnalyticsViewsDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: LIST_SESSIONS_NAME,
    adapt: ({ output, source }) => buildListSessionsDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: LIST_URL_GROUPS_NAME,
    adapt: ({ output, source }) => buildListUrlGroupsDatasets(output, source),
  }),
  defineVisualizationAdapter({
    toolName: LIST_USER_ANALYTICS_VIEWS_NAME,
    adapt: ({ output, source }) =>
      buildListUserAnalyticsViewsDatasets(output, source),
  }),
] as const;

const visualizationAdapterRegistry = createVisualizationAdapterRegistry(
  visualizationAdapters as readonly VisualizationAdapter<VizSourceToolName>[],
);

/**
 * 可視化データソースとして明示登録された tool 名集合。
 */
export const VISUALIZATION_SOURCE_TOOL_NAMES =
  visualizationAdapterRegistry.sourceToolNames;

/**
 * 可視化データソースかどうかを判定する。
 */
export function isVisualizationSourceToolName(toolName: string): boolean {
  if (toolName === VIZ_TOOL_NAME) return false;
  return VISUALIZATION_SOURCE_TOOL_NAMES.has(toolName as VizSourceToolName);
}

/**
 * tool 名から adapter を解決する。未登録なら null を返す。
 */
export function resolveVisualizationAdapter(
  toolName: string,
): VisualizationAdapter<VizSourceToolName> | null {
  if (toolName === VIZ_TOOL_NAME) return null;
  return visualizationAdapterRegistry.resolve(toolName);
}

/**
 * snapshot を adapter へ渡して dataset を構築する。
 *
 * @remarks
 * runtime 検証は adapter 側で実施するため、ここでは tool 名に対応する型として渡すだけにする。
 */
export function adaptSnapshotToDatasets(
  snapshot: VizSourceSnapshot,
): VizDataset[] {
  if (snapshot.toolName === VIZ_TOOL_NAME) return [];
  return visualizationAdapterRegistry.adaptSnapshot(snapshot);
}
